/**
 * FrameJitterOverlayContent (LRG.20) — UI for the FrameJitterTracker.
 *
 * Shows rAF gap distribution: histogram + percentiles + spike log.
 * Default OFF. Operator flips checkbox to enable.
 *
 * data-testid="frame-jitter-overlay" + data-{p50,p95,p99,max} attrs.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { FrameJitterTracker, type FrameJitterStats } from '../../../diagnostics/FrameJitterTracker';
import { ReactRenderTracker } from '../../../diagnostics/ReactRenderTracker';
import { CopyButton } from '../CopyButton';

const POLL_MS = 500;

const TARGET_MS = 1000 / 60;  // ~16.67ms

function colorForMs(ms: number): string {
  if (ms < 17) return '#42d77d';
  if (ms < 20) return '#fa5';
  return '#ff5f5f';
}

interface ReactRenderStats {
  enabled: boolean;
  rows: Array<{ id: string; rendersPerSec: number; totalRenders: number; windowMs: number }>;
}

function renderColorForRate(r: number): string {
  if (r < 15) return '#42d77d';
  if (r < 30) return '#fa5';
  return '#ff5f5f';
}

export const FrameJitterOverlayContent: React.FC = () => {
  const [stats, setStats] = useState<FrameJitterStats>(() => FrameJitterTracker.getStats());
  const [renderStats, setRenderStats] = useState<ReactRenderStats>(() => ({
    enabled: ReactRenderTracker.enabled,
    rows: ReactRenderTracker.getStats(),
  }));

  useEffect(() => {
    const id = setInterval(() => {
      setStats(FrameJitterTracker.getStats());
      setRenderStats({ enabled: ReactRenderTracker.enabled, rows: ReactRenderTracker.getStats() });
    }, POLL_MS);
    return () => clearInterval(id);
  }, []);

  const toggleEnabled = useCallback(() => {
    FrameJitterTracker.setEnabled(!stats.enabled);
    setStats(FrameJitterTracker.getStats());
  }, [stats.enabled]);

  const toggleRenderTracking = useCallback(() => {
    ReactRenderTracker.setEnabled(!renderStats.enabled);
    setRenderStats({ enabled: ReactRenderTracker.enabled, rows: ReactRenderTracker.getStats() });
  }, [renderStats.enabled]);

  const clearRenderStats = useCallback(() => {
    ReactRenderTracker.clear();
    setRenderStats({ enabled: ReactRenderTracker.enabled, rows: ReactRenderTracker.getStats() });
  }, []);

  const clear = useCallback(() => {
    FrameJitterTracker.clear();
    setStats(FrameJitterTracker.getStats());
  }, []);

  const copyText = () => {
    const lines = [
      `FRAME JITTER ANALYZER @ ${new Date().toISOString()}`,
      `enabled:    ${stats.enabled}`,
      `samples:    ${stats.sampleCount} (last ${stats.windowMs}ms)`,
      `min:        ${stats.min.toFixed(2)} ms`,
      `mean:       ${stats.mean.toFixed(2)} ms`,
      `p50:        ${stats.p50.toFixed(2)} ms`,
      `p95:        ${stats.p95.toFixed(2)} ms`,
      `p99:        ${stats.p99.toFixed(2)} ms`,
      `max:        ${stats.max.toFixed(2)} ms`,
      `target:     ${TARGET_MS.toFixed(2)} ms (60fps)`,
      `spikes10s:  ${stats.spikesLast10s} (>25ms)`,
      ``,
      `histogram:`,
      ...stats.histogram.map(b => `  ${b.bucket.padEnd(8)}: ${b.count}`),
      ``,
      `recent spikes (last 10):`,
      ...stats.recentSpikes.map(s => `  ${new Date(s.at + Date.now() - performance.now()).toISOString()}  ${s.deltaMs.toFixed(2)}ms p2=${s.phase2Ms?.toFixed(1) ?? '?'}`),
      ``,
      `react render rates (tracking=${renderStats.enabled}):`,
      ...(renderStats.rows.length === 0
        ? ['  (no components tracked yet — wait 10s or check tracker is enabled)']
        : renderStats.rows.map(r => `  ${r.id.padEnd(25)}: ${r.rendersPerSec.toFixed(1).padStart(6)}/s  (${r.totalRenders} renders over ${(r.windowMs / 1000).toFixed(1)}s)`)),
    ];
    return lines.join('\n');
  };

  const headerStyle: React.CSSProperties = {
    color: '#7af',
    marginBottom: 4,
    letterSpacing: '0.05em',
    fontSize: 10,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  return (
    <div
      data-testid="frame-jitter-overlay"
      data-enabled={String(stats.enabled)}
      data-samples={stats.sampleCount}
      data-p50={stats.p50.toFixed(2)}
      data-p95={stats.p95.toFixed(2)}
      data-p99={stats.p99.toFixed(2)}
      data-max={stats.max.toFixed(2)}
      data-spikes-10s={stats.spikesLast10s}
      style={{
        color: '#ddd',
        fontFamily: 'monospace',
        fontSize: 11,
        padding: '6px 8px',
        userSelect: 'none',
        lineHeight: 1.4,
      }}
    >
      <div style={headerStyle}>
        <span>FRAME JITTER · rAF gap analyzer</span>
        <CopyButton getText={copyText} />
      </div>

      <label
        data-testid="frame-jitter-toggle"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 6px',
          borderRadius: 3,
          border: `1px solid ${stats.enabled ? '#42d77d' : '#666'}`,
          background: stats.enabled ? 'rgba(66,215,125,0.10)' : 'rgba(255,255,255,0.04)',
          color: stats.enabled ? '#42d77d' : '#aaa',
          fontSize: 10,
          fontWeight: 600,
          cursor: 'pointer',
          marginBottom: 6,
        }}
      >
        <input
          type="checkbox"
          checked={stats.enabled}
          onChange={toggleEnabled}
          data-testid="frame-jitter-checkbox"
          style={{ accentColor: stats.enabled ? '#42d77d' : '#666', cursor: 'pointer' }}
        />
        <span>{stats.enabled ? '✅ TRACKING' : '⏸ paused (default)'}</span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={clear}
          data-testid="frame-jitter-clear"
          style={{
            padding: '1px 6px',
            fontSize: 9,
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 3,
            background: 'transparent',
            color: '#aaa',
            cursor: 'pointer',
          }}
        >
          clear
        </button>
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 8, fontSize: 10, marginBottom: 6 }}>
        <span style={{ color: '#888' }}>target</span>
        <span>{TARGET_MS.toFixed(2)}ms (60fps)</span>
        <span style={{ color: '#888' }}>samples</span>
        <span>{stats.sampleCount} ({Math.round(stats.windowMs / 1000)}s window)</span>
        <span style={{ color: '#888' }}>min · mean</span>
        <span>
          <span style={{ color: colorForMs(stats.min) }}>{stats.min.toFixed(1)}</span>
          {' · '}
          <span style={{ color: colorForMs(stats.mean) }}>{stats.mean.toFixed(1)}</span>
          {' ms'}
        </span>
        <span style={{ color: '#888' }}>p50 · p95 · p99 · max</span>
        <span>
          <span style={{ color: colorForMs(stats.p50) }}>{stats.p50.toFixed(1)}</span>
          {' · '}
          <span style={{ color: colorForMs(stats.p95) }}>{stats.p95.toFixed(1)}</span>
          {' · '}
          <span style={{ color: colorForMs(stats.p99) }}>{stats.p99.toFixed(1)}</span>
          {' · '}
          <span style={{ color: colorForMs(stats.max) }}>{stats.max.toFixed(1)}</span>
          {' ms'}
        </span>
        <span style={{ color: '#888' }}>spikes &gt;25ms</span>
        <span style={{ color: stats.spikesLast10s > 0 ? '#fa5' : '#42d77d' }}>{stats.spikesLast10s} in last 10s</span>
      </div>

      <div style={{ color: '#7af', fontSize: 9, marginBottom: 2 }}>HISTOGRAM · {stats.windowMs / 1000}s window</div>
      <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 40px', columnGap: 4, fontSize: 9 }}>
        {stats.histogram.map(b => {
          const maxBar = Math.max(1, ...stats.histogram.map(x => x.count));
          const pct = b.count / maxBar;
          const isOverBudget = b.threshold > 18;
          const barColor = isOverBudget ? '#fa5' : '#42d77d';
          return (
            <React.Fragment key={b.bucket}>
              <span style={{ color: '#aaa' }}>{b.bucket}</span>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 2, position: 'relative', height: 10 }}>
                <div style={{
                  width: `${pct * 100}%`,
                  background: barColor,
                  height: '100%',
                  borderRadius: 2,
                }} />
              </div>
              <span style={{ textAlign: 'right', color: b.count > 0 ? '#ccc' : '#666' }}>{b.count}</span>
            </React.Fragment>
          );
        })}
      </div>

      {stats.recentSpikes.length > 0 && (
        <>
          <div style={{ color: '#fa5', fontSize: 9, margin: '6px 0 2px' }}>RECENT SPIKES &gt;25ms</div>
          {stats.recentSpikes.slice(-5).reverse().map((s, i) => (
            <div key={`spike-${i}-${s.at}`} style={{ fontSize: 9, color: '#999' }}>
              <span style={{ color: '#fa5' }}>{s.deltaMs.toFixed(1)}ms</span>
              {s.phase2Ms != null && <span> · p2={s.phase2Ms.toFixed(1)}</span>}
            </div>
          ))}
        </>
      )}

      <div style={{ color: '#7af', fontSize: 9, margin: '8px 0 2px' }}>REACT RENDER RATES</div>
      <label
        data-testid="react-render-toggle"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '3px 6px',
          borderRadius: 3,
          border: `1px solid ${renderStats.enabled ? '#42d77d' : '#666'}`,
          background: renderStats.enabled ? 'rgba(66,215,125,0.10)' : 'rgba(255,255,255,0.04)',
          color: renderStats.enabled ? '#42d77d' : '#aaa',
          fontSize: 9,
          fontWeight: 600,
          cursor: 'pointer',
          marginBottom: 4,
        }}
      >
        <input
          type="checkbox"
          checked={renderStats.enabled}
          onChange={toggleRenderTracking}
          data-testid="react-render-checkbox"
          style={{ accentColor: renderStats.enabled ? '#42d77d' : '#666', cursor: 'pointer' }}
        />
        <span>{renderStats.enabled ? '✅ TRACKING' : '⏸ paused (default)'}</span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={clearRenderStats}
          data-testid="react-render-clear"
          style={{
            padding: '1px 6px',
            fontSize: 9,
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 3,
            background: 'transparent',
            color: '#aaa',
            cursor: 'pointer',
          }}
        >
          clear
        </button>
      </label>
      {renderStats.enabled && renderStats.rows.length === 0 && (
        <div style={{ fontSize: 9, color: '#888' }}>
          enabled — waiting for HUD components to render…
        </div>
      )}
      {renderStats.enabled && renderStats.rows.length > 0 && (
        <div
          data-testid="react-render-rows"
          style={{
            display: 'grid',
            gridTemplateColumns: '100px 50px 1fr',
            columnGap: 4,
            fontSize: 9,
          }}
        >
          {renderStats.rows.map((row) => {
            const color = renderColorForRate(row.rendersPerSec);
            return (
              <React.Fragment key={row.id}>
                <span style={{ color: '#ccc' }} data-testid={`react-render-row-${row.id}`}>
                  {row.id}
                </span>
                <span style={{ color, textAlign: 'right' }}>
                  {row.rendersPerSec.toFixed(1)}/s
                </span>
                <span style={{ color: '#666' }}>
                  {row.totalRenders} renders · {Math.round(row.windowMs / 1000)}s
                </span>
              </React.Fragment>
            );
          })}
        </div>
      )}
      {!renderStats.enabled && (
        <div style={{ fontSize: 9, color: '#666' }}>
          flip on to expose renders/sec per HUD component. Pair with frame
          jitter spikes — anything sustaining &gt;30/s is a React.memo candidate.
        </div>
      )}
    </div>
  );
};
